// services/memoirService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 15000
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('kapta_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't blow away the token on public endpoints
      const url = error.config?.url || '';
      const isPublic =
        url.includes('/categories') || url.includes('/track');

      if (!isPublic) {
        localStorage.removeItem('kapta_token');
        localStorage.removeItem('kapta_user');
        // Only redirect if we're on a protected page
        const path = window.location.pathname;
        if (path.startsWith('/user/')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

/* ── Small helper to standardise error messages ── */
const wrap = async (promise, fallback = 'Request failed') => {
  try {
    const response = await promise;
    return response.data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      fallback;
    // Preserve the full error so callers can inspect `.response`
    const wrapped = new Error(message);
    wrapped.response = error.response;
    throw wrapped;
  }
};

export const memoirService = {
  /* ═══════════════════════════════════════════════════════════════
     CATEGORIES (Public)
     ═══════════════════════════════════════════════════════════════ */

  /**
   * Get all categories (public)
   */
  async getCategories() {
    return wrap(api.get('/api/v1/memoirs/categories'), 'Failed to get categories');
  },

  /**
   * Get category by ID with its questions (public)
   */
  async getCategoryById(id) {
    return wrap(
      api.get(`/api/v1/memoirs/categories/${id}`),
      'Failed to get category'
    );
  },

  /* ═══════════════════════════════════════════════════════════════
     ORDER CREATION (Public / optional auth)
     ═══════════════════════════════════════════════════════════════ */

  /**
   * Create a new memoir order (with payment initialization)
   * Returns { order, payment: { reference, authorizationUrl, accessCode, transactionId } }
   */
  async createOrder(orderData) {
    return wrap(
      api.post('/api/v1/memoirs/orders', orderData),
      'Failed to create order'
    );
  },

  /* ═══════════════════════════════════════════════════════════════
     USER MEMOIRS (Auth required)
     ═══════════════════════════════════════════════════════════════ */

  /**
   * List all memoirs belonging to the logged-in user.
   * Used by the sidebar in MemiorLayout.
   * Returns: [{ id, title, type, chapters, status, completedWeeks, lastResponse, ... }]
   */
  async getMyMemoirs() {
    return wrap(
      api.get('/api/v1/memoirs/mine'),
      'Failed to get memoirs'
    );
  },

  /**
   * Alias kept for backwards compatibility with existing imports
   * that call `getUserMemoirs()`.
   */
  async getUserMemoirs() {
    return this.getMyMemoirs();
  },

  /**
   * Get a single memoir by ID (owner only).
   * Returns the full memoir payload: title, phase, progressItems,
   * nextPrompt, scheduledFor, questions, etc.
   */
  async getMemoirById(id) {
    return wrap(
      api.get(`/api/v1/memoirs/orders/${id}`),
      'Failed to get memoir'
    );
  },

  /* ═══════════════════════════════════════════════════════════════
     CHAPTERS (Auth required)
     ═══════════════════════════════════════════════════════════════ */

  /**
   * List all chapters (weeks) for a memoir.
   * Returns: { memoirId, title, totalWeeks, currentWeek, chapters: [{ week, title, status, isLocked }] }
   */
  async getChapters(memoirId) {
    return wrap(
      api.get(`/api/v1/memoirs/orders/${memoirId}/chapters`),
      'Failed to get chapters'
    );
  },

  /**
   * Get a single chapter (week) with its questions.
   * Returns: { memoirId, week, title, status, questions: [...], responses: [] }
   */
  async getChapter(memoirId, week) {
    return wrap(
      api.get(`/api/v1/memoirs/orders/${memoirId}/chapters/${week}`),
      'Failed to get chapter'
    );
  },

  /* ═══════════════════════════════════════════════════════════════
     PROGRESS (Auth required)
     ═══════════════════════════════════════════════════════════════ */

  /**
   * Get progress snapshot for a memoir.
   * Returns: { currentWeek, totalWeeks, weeksCompleted, progressPercentage, weeks: [...] }
   */
  async getProgress(memoirId) {
    return wrap(
      api.get(`/api/v1/memoirs/orders/${memoirId}/progress`),
      'Failed to get progress'
    );
  },

  /* ═══════════════════════════════════════════════════════════════
     STORY SUBMISSION (Auth required)
     ═══════════════════════════════════════════════════════════════ */

  /**
   * Submit a story/response for a given week.
   * @param {string} memoirId
   * @param {Object} payload - { weekNumber, questionId?, text?, audioUrl? }
   */
  async submitStory(memoirId, payload) {
    return wrap(
      api.post(`/api/v1/memoirs/orders/${memoirId}/story`, payload),
      'Failed to submit story'
    );
  },

  /* ═══════════════════════════════════════════════════════════════
     GUEST TRACKING (Public)
     ═══════════════════════════════════════════════════════════════ */

  /**
   * Track a guest memoir order by email + order number.
   * Returns a minimal summary the guest can view without logging in.
   */
  async trackGuestOrder(email, orderNumber) {
    return wrap(
      api.post('/api/v1/memoirs/track', { email, orderNumber }),
      'Failed to track order'
    );
  }
};

export default memoirService;